import type { OrderDetails } from '@/entities/order/model/order.types'
import { Box, Button, Center, Flex, LoadingOverlay, Modal, Stack, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconChevronLeft, IconSettings, IconX } from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'
import { clsx } from 'clsx'
import { useEffect, useState } from 'react'
import { useAcknowledgeOrder } from '@/entities/order/lib'
import { useOrderStatusMutation, useRawOrdersQuery } from '@/entities/order/model'
import { useOrderDetails, useOrderGroupMembers } from '@/features/order/lib'
import { useResponsiveModal } from '@/shared/lib/mantine'
import headerIconButtonClasses from '@/shared/ui/component-styles/HeaderIconButton.module.css'
import modalClasses from '@/shared/ui/component-styles/Modal.module.css'
import { OrderDetailsDesktopLayout } from '../desktop-layout/OrderDetailsDesktopLayout'
import { OrderDetailsMobileLayout } from '../mobile-layout/OrderDetailsMobileLayout'
import { OrderSettingsMenu } from '../settings-menu/OrderSettingsMenu'
import { StatusUpdateDrawer } from '../status-drawer/StatusUpdateDrawer'
import { CancelOrderConfirmationModal } from './cancel-confirmation'
import classes from './OrderDetailsModal.module.css'

interface OrderDetailsModalProps {
  orderId: OrderDetails['orderId']
  parentPath: string
  withoutBackdrop?: boolean
}

/**
 * Modal component for displaying order details (Aligns with Coles-specific design)
 */
export function OrderDetailsModal({
  orderId,
  parentPath,
  withoutBackdrop = false,
}: OrderDetailsModalProps) {
  const navigate = useNavigate()
  const { order, isLoading } = useOrderDetails(orderId)
  const { updateOrderAcknowledgement } = useAcknowledgeOrder()
  const updateOrderStatus = useOrderStatusMutation()
  const { data: allOrders } = useRawOrdersQuery()

  const [modalOpened, setModalOpened] = useState(true)
  const modalConfig = useResponsiveModal()
  const [statusDrawerOpened, { open: openStatusDrawer, close: closeStatusDrawer }] = useDisclosure(false)
  const [cancelModalOpened, { open: openCancelModal, close: closeCancelModal }] = useDisclosure(false)

  const handleClose = () => {
    setModalOpened(false)
    navigate({
      to: parentPath,
      search: {},
      resetScroll: false,
    })
  }

  const handleCancelOrder = async () => {
    if (!order) {
      return
    }

    try {
      await updateOrderStatus.mutateAsync({
        orderId: order.orderId,
        newStatus: 'cancelled',
        lastUpdateTime: order.lastUpdateTime,
      })

      closeCancelModal()
      handleClose()
    } catch (error) {
      console.error('Failed to cancel the order:', error)
    }
  }

  const groupMemberOrderIds = useOrderGroupMembers(orderId)

  useEffect(() => {
    if (order && !globalThis.window.__tutorialMode) {
      const acknowledgeAllOrders = async () => {
        try {
          const orderMap = allOrders?.reduce((acc: Record<string, OrderDetails>, o: OrderDetails) => {
            acc[o.orderId] = o
            return acc
          }, {} as Record<string, OrderDetails>) || {}

          const acknowledgmentPromises = groupMemberOrderIds.map((id) => {
            const orderToUse = orderMap[id] || {
              orderId: id,
              status: order.status,
              isAcknowledged: false,
              lastUpdateTime: order.lastUpdateTime,
            }
            return updateOrderAcknowledgement(orderToUse as OrderDetails)
          })

          await Promise.all(acknowledgmentPromises)
        } catch (error) {
          console.error('Failed to acknowledge orders in group:', error)
        }
      }

      acknowledgeAllOrders()
    }
    // eslint-disable-next-line react/exhaustive-deps
  }, [orderId])

  return (
    <Modal
      opened={modalOpened}
      onClose={handleClose}
      title={null}
      size={modalConfig.size}
      fullScreen={modalConfig.fullScreen}
      transitionProps={modalConfig.transitionProps}
      classNames={{
        content: modalClasses.modalContent,
        body: modalClasses.modalBody,
      }}
      closeOnClickOutside={!withoutBackdrop}
      overlayProps={withoutBackdrop ? { backgroundOpacity: 0, blur: 0 } : undefined}
      data-onboarding="order-details-modal"
    >
      {order && (
        <div className={clsx(
          modalClasses.modalHeader,
          !modalConfig.isSmUp
            ? modalClasses.mobileHeader
            : modalClasses.desktopHeader,
        )}
        >
          {!modalConfig.isSmUp ? (
            <>
              <button
                type="button"
                aria-label="Go back"
                className={headerIconButtonClasses.iconButton}
                onClick={handleClose}
              >
                <div className={headerIconButtonClasses.iconButtonInner}>
                  <IconChevronLeft
                    className={headerIconButtonClasses.icon}
                    size={32}
                  />
                </div>
              </button>

              <h3 className={modalClasses.headerTitle}>{order.customerName}</h3>

              <OrderSettingsMenu
                renderTrigger={({ onClick: _onClick }) => (
                  <button
                    type="button"
                    aria-label="Settings"
                    className={headerIconButtonClasses.iconButton}
                    onClick={_onClick}
                  >
                    <div className={headerIconButtonClasses.iconButtonInner}>
                      <IconSettings
                        className={headerIconButtonClasses.icon}
                        size={32}
                      />
                    </div>
                  </button>
                )}
                onCancelOrder={openCancelModal}
              />
            </>
          ) : (
            <>
              {withoutBackdrop ? (
                <>
                  <button
                    type="button"
                    aria-label="Close modal"
                    className={headerIconButtonClasses.iconButton}
                    onClick={handleClose}
                  >
                    <div className={headerIconButtonClasses.iconButtonInner}>
                      <IconChevronLeft
                        className={headerIconButtonClasses.icon}
                        size={32}
                      />
                    </div>
                  </button>

                  <h3 className={modalClasses.headerTitle}>{order.customerName}</h3>
                </>
              ) : (
                <>
                  <h3 className={modalClasses.headerTitle}>{order.customerName}</h3>

                  <button
                    type="button"
                    aria-label="Close modal"
                    className={headerIconButtonClasses.iconButton}
                    onClick={handleClose}
                  >
                    <div className={headerIconButtonClasses.iconButtonInner}>
                      <IconX
                        className={headerIconButtonClasses.icon}
                        size={32}
                      />
                    </div>
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}

      {isLoading ? (
        <Center h={300}>
          <LoadingOverlay visible />
          <Text span c="dimmed">Loading order details...</Text>
        </Center>
      ) : !order ? (
        <Center h={300}>
          <Stack gap="md" align="center">
            <Text span c="dimmed">Could not find order details</Text>
            <Button onClick={handleClose} size="sm">Close</Button>
          </Stack>
        </Center>
      ) : (
        <>
          <Box className={modalClasses.content}>
            {!modalConfig.isSmUp ? (
              <OrderDetailsMobileLayout
                order={order}
                withoutBackdrop={withoutBackdrop}
              />
            ) : (
              <OrderDetailsDesktopLayout
                order={order}
                withoutBackdrop={withoutBackdrop}
              />
            )}
          </Box>

          <Box className={classes.actions}>
            {!modalConfig.isSmUp ? (
              <Button
                variant="filled"
                color="gray.8"
                fullWidth
                size="lg"
                radius="md"
                onClick={openStatusDrawer}
                data-onboarding="update-status-button"
              >
                Update status
              </Button>
            ) : (
              <Flex justify="space-between" align="center" w="100%">
                <OrderSettingsMenu
                  renderTrigger={({ onClick: _onClick }) => (
                    <Button
                      variant="subtle"
                      color="gray.8"
                      size="md"
                      radius="md"
                      leftSection={<IconSettings size={24} />}
                      onClick={_onClick}
                    >
                      Options
                    </Button>
                  )}
                  onCancelOrder={openCancelModal}
                  position="top-start"
                />
                <Button
                  variant="filled"
                  color="gray.8"
                  size="md"
                  radius="md"
                  className={classes.actionsDesktop}
                  onClick={handleClose}
                >
                  Done
                </Button>
              </Flex>
            )}
          </Box>
        </>
      )}

      {/* Status Update Drawer - Only for single column layout */}
      {!modalConfig.isSmUp && order && (
        <StatusUpdateDrawer
          opened={statusDrawerOpened}
          onClose={closeStatusDrawer}
          orderId={order.orderId}
          currentStatus={order.status}
          lastUpdateTime={order.lastUpdateTime}
        />
      )}

      {order && (
        <CancelOrderConfirmationModal
          opened={cancelModalOpened}
          onClose={closeCancelModal}
          onConfirm={handleCancelOrder}
          customerName={order.customerName || 'Unknown Customer'}
          isLoading={updateOrderStatus.isPending}
        />
      )}
    </Modal>
  )
}
